<template>
  <div>
    <div class="w-full px-5">
      <header class="flex items-center justify-between gap-4 flex-wrap mt-3">
        <div class="flex items-end gap-3 relative px-1">
          <a :href="getRemoteUrl('/organizer')"><AppLogo height="3rem" /></a>
          <span
            class="text-xs tracking-wider uppercase text-base-content/60 absolute top-[39px] left-[68px]"
          >
            DICOM anonymization
          </span>
        </div>

        <div class="flex flex-col items-right text-right">
          <UserSession></UserSession>
          <div class="flex justify-end items-center gap-3">
            <a v-if="userSession?._id" :href="getRemoteUrl('/organizer')"
              >Back to cases</a
            >
            <span
              v-if="userSession?._id"
              class="h-4 w-px bg-gray-700/50"
              aria-hidden="true"
            ></span>
            <ThemeToggle />
          </div>
          <!-- <span v-if="buildMode !== 'production'" class="opacity-50
          text-xs"> -->
          <span class="text-xs text-base-content/20">
            {{ buildMode }} · v{{ packageVersion
            }}<!--  · pacsbin
            {{ pacsbinVersion }} -->
          </span>
        </div>
      </header>

      <nav class="flex gap-1 border-b border-base-300 mt-3 mb-4">
        <button
          v-for="t in tabs"
          :key="t.key"
          type="button"
          class="px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors"
          :class="
            tab === t.key
              ? 'border-primary text-primary'
              : 'cursor-pointer border-transparent text-base-content/60 hover:text-base-content hover:border-base-300'
          "
          @click="tab = t.key"
        >
          {{ t.label }}
        </button>
      </nav>
      <div v-if="tab === 'process-files'" class="flex mb-10 pb-12">
        <div class="w-1/2 pr-5">
          <div>
            <section class="flex gap-3 items-center mb-3">
              <div v-if="caseDef.studies.length > 0" class="py-1 shrink-0">
                <button class="btn btn-error btn-soft" @click="reloadPage">
                  Start over
                </button>
              </div>
              <div class="w-full">
                <div
                  v-if="isLoggedOut"
                  class="border border-info/30 bg-info/5 py-3 px-4"
                >
                  <template v-if="step === 'load'">
                    <div class="flex items-start gap-2">
                      <Icon
                        icon="feather:info"
                        class="text-lg text-info shrink-0 mt-0.5"
                      ></Icon>
                      <div class="text-base-content/80 text-sm">
                        You're not logged in — you can still anonymize DICOM
                        files and download them as a ZIP to your computer.
                        <a :href="getRemoteUrl('login')">Log in</a> if you'd
                        like to upload studies to Pacsbin instead.
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="flex items-start gap-2">
                      <Icon
                        icon="feather:info"
                        class="text-lg text-info shrink-0 mt-0.5"
                      ></Icon>
                      <span class="text-base-content/80 text-sm"
                        >Not logged in — you can download processed files to
                        your computer.
                        <a :href="getRemoteUrl('login')">Log in</a> to enable
                        uploading to Pacsbin.</span
                      >
                    </div>
                  </template>
                </div>
                <div
                  v-if="isLoggedIn && !hasRoomInAccount"
                  class="border border-error bg-error/10 p-8 space-y-2"
                >
                  <div>
                    <Icon
                      icon="feather:alert-triangle"
                      class="text-2xl inline text-error mr-2"
                    ></Icon>
                    <span class="text-error text-lg inline-block align-middle"
                      >Wait!</span
                    >
                  </div>
                  <p>
                    You have used {{ studyCount }} of
                    {{ maxStudies === -1 ? "unlimited" : maxStudies }} studies.
                    If you would like to upload more studies, you must first
                    either upgrade your account or delete some existing studies,
                    then reload this page.
                  </p>
                  <p>
                    You can still export a ZIP file of processed DICOM to your
                    own computer.
                  </p>
                </div>
                <div
                  v-if="isLoggedIn && hasRoomInAccount"
                  class="border border-success bg-success/10 p-2"
                >
                  You have used {{ studyCount }} of
                  {{ maxStudies === -1 ? "unlimited" : maxStudies }} studies.
                </div>
              </div>
            </section>
            <WorkflowStepper :step="step" class="px-3" />
            <section v-if="step === 'load'">
              <!-- <h3 class="section-head">
                Load DICOM files
              </h3> -->
              <div class="section-content">
                <!-- <p class="mb-2">Select files from your computer:</p> -->
                <DicomWeb
                  v-if="orgId && orgConfig"
                  :org-config="orgConfig"
                  :load-sources="loadRetrievedSources"
                  @complete="advanceAfterLoad"
                ></DicomWeb>
                <ImportLocal
                  v-else
                  :case-def="caseDef"
                  :loading="isLoadingFiles"
                  @load-files="loadFilesAndAdvance"
                ></ImportLocal>
              </div>
            </section>
            <section v-if="step === 'redact'">
              <!-- <h3 class="section-head">
                Edit &amp; redact images
              </h3> -->
              <div class="section-content">
                <QuarantinedSeriesList
                  :case-def="caseDef"
                  :selected-series="selectedSeries"
                  @select-series="setSelectedSeries"
                ></QuarantinedSeriesList>
                <!-- <div class="mt-10 shadow-inner border border-gray-200 bg-gray-100 p-5">
                  <span class="font-bold opacity-60">Action on anonymization error</span>
                  <div class="pl-5">
                    <input
                      type="radio"
                      id="lenient"
                      v-bind:value="false"
                      style="position: relative; top: -1px"
                      v-model="scriptRef.options.strict"
                    />
                    <label class="ml-2" for="lenient">
                      Empty tag and continue
                      <span class="opacity-50">(Default)</span>
                    </label>
                    <br />
                    <input
                      type="radio"
                      id="strict"
                      v-bind:value="true"
                      style="position: relative; top: -1px"
                      v-model="scriptRef.options.strict"
                    />
                    <label class="ml-2" for="strict">
                      Stop anonymization
                      <span class="opacity-50">(Remove file)</span>
                    </label>
                  </div>
                </div>-->
                <div>
                  <button
                    type="button"
                    class="btn btn-primary mt-5"
                    @click="finalizeAndPrepareForExport"
                    :disabled="instancesToProcess === 0"
                  >
                    Apply image edits/redactions and anonymize
                  </button>
                  <div
                    v-if="instancesToProcess === 0"
                    class="mt-2 text-sm text-error"
                  >
                    No images are currently marked for processing. Images may be
                    quarantined or marked for removal.
                  </div>
                </div>
              </div>
            </section>
            <section v-if="step === 'review'">
              <!-- <h3 class="section-head">
                Review anonymization
              </h3> -->
              <div class="section-content">
                Review image edits to ensure all pixel data PHI has been
                redacted, if necessary.
                <div
                  class="my-4 border p-3 text-sm"
                  :class="
                    redactionSummary.instances > 0
                      ? 'border-success/30 bg-success/5'
                      : 'border-base-300 bg-base-200/40'
                  "
                >
                  <template v-if="redactionSummary.instances > 0">
                    <div class="font-medium">
                      Pixel redaction applied to
                      {{ redactionSummary.instances }}
                      {{ redactionSummary.instances === 1 ? "image" : "images" }}
                      in {{ redactionSummary.series }}
                      {{ redactionSummary.series === 1 ? "series" : "series" }}.
                    </div>
                    <!-- <div class="text-base-content/70 mt-1">
                      Each written frame was checked against its redaction boxes
                      as the file was saved — if a redacted region had not been
                      blanked, the export would have stopped instead.
                    </div> -->
                  </template>
                  <template v-else>
                    <div class="font-medium">
                      No pixel redactions were applied to this case.
                    </div>
                    <div class="text-base-content/70 mt-1">
                      If you expected redactions, stop here and start over — the
                      files have already been written without them.
                    </div>
                  </template>
                </div>
                <a
                  class="block my-5"
                  href="#"
                  @click.prevent="tab = 'values-histogram'"
                >
                  Review a summary of the anonymized DICOM header values to
                  ensure all PHI has been removed or replaced.
                </a>
                <div
                  class="btn btn-primary"
                  @click="
                    () => {
                      caseDef.phiAttestation = new Date();
                      nextStep();
                    }
                  "
                >
                  Attest no PHI in pixel data or DICOM headers
                </div>
              </div>
            </section>
            <section v-if="step === 'export'">
              <!-- <h3 class="section-head">
                Export studies
              </h3> -->
              <div class="section-content">
                <div class="flex gap-1 border-b border-base-300 mb-4">
                  <button
                    type="button"
                    class="px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors"
                    :class="
                      exportDestination === 'pacsbin'
                        ? 'border-primary text-primary'
                        : 'cursor-pointer border-transparent text-base-content/60 hover:text-base-content hover:border-base-300'
                    "
                    @click="exportDestination = 'pacsbin'"
                  >
                    Upload to Pacsbin
                  </button>
                  <button
                    type="button"
                    class="px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors"
                    :class="
                      exportDestination === 'zip'
                        ? 'border-primary text-primary'
                        : 'cursor-pointer border-transparent text-base-content/60 hover:text-base-content hover:border-base-300'
                    "
                    @click="exportDestination = 'zip'"
                  >
                    Download as ZIP
                  </button>
                </div>
                <ZipDownload
                  v-if="exportDestination === 'zip'"
                  :case-def="caseDef"
                ></ZipDownload>
                <PacsbinUpload
                  v-if="exportDestination === 'pacsbin'"
                  :case-def="caseDef"
                ></PacsbinUpload>
                <!-- <div class="section-content">
                  <div class="btn" @click.prevent="downloadAll">Download all</div>
                </div>-->
              </div>
            </section>
          </div>
        </div>
        <div class="w-1/2">
          <CaseEditor
            ref="caseEditor"
            :selected-series="selectedSeries"
            :editable="step === 'redact'"
            :case-def="caseDef"
            @set-selected-series="setSelectedSeries"
          ></CaseEditor>
          <div
            v-if="caseDef.phiAttestation"
            class="bg-success/20 text-success p-2 mb-2"
          >
            Attested no PHI present: {{ caseDef.phiAttestation }}
          </div>
        </div>
      </div>
      <div
        v-if="tab === 'anonymization-script'"
        id="anonymization-script"
        class="relative"
      >
        <div class="w-full md:w-1/2 mx-auto mt-10">
          <p class="text-sm mb-5">
            This anonymizer supports a subset of the operations implemented by
            the RSNA CTP anonymizer. The CTP anonymizer script format has been
            converted to JSON, however follows the same basic structure. See the
            <a
              href="https://mircwiki.rsna.org/index.php?title=The_CTP_DICOM_Anonymizer"
              >CTP anonymizer</a
            >
            reference for more information.
          </p>
          <div class="relative">
            <button
              type="button"
              class="btn absolute right-2 top-2"
              :title="scriptCopied ? 'Copied!' : 'Copy to clipboard'"
              @click="copyScriptToClipboard"
            >
              <Icon
                :icon="scriptCopied ? 'feather:check' : 'feather:copy'"
              ></Icon>
            </button>
            <pre class="text-xs bg-base-200 p-2 border border-base-300">{{
              JSON.stringify(defaultScript, null, 4)
            }}</pre>
          </div>
        </div>
      </div>
      <div
        v-if="tab === 'values-histogram'"
        id="values-histogram"
        class="relative"
      >
        <div class="w-full md:w-1/2 mx-auto mt-10">
          <p class="mb-5">
            <a href="#" @click.prevent="tab = 'process-files'"
              >Back to file processing</a
            >
          </p>
          <p class="text-sm mb-5">
            All unique DICOM header values contained in the current datasets:
          </p>
          <ValuesHistogram :instances="allInstances"></ValuesHistogram>
        </div>
      </div>
      <div v-if="tab === 'about'" class="relative">
        <div class="w-full md:w-1/2 mx-auto mt-10">
          <h1 class="text-lg font-bold">About this application</h1>
          <p class="my-5">
            Thanks to the steady advancement of Javascript and web browser
            technologies, as well as lots of open source work by the brilliant
            folks behind the
            <a href="https://cornerstonejs.org/" target="_blank"
              >CornerstoneJS</a
            >, <a href="https://ohif.org/" target="_blank">OHIF</a>, and
            <a href="https://github.com/dcmjs-org/dcmjs" target="_blank"
              >DCMJS</a
            >
            projects, manipulation of DICOM images is now possible entirely
            within the browser.
          </p>
          <p class="my-5">
            This means that DICOM images can now be parsed, modified, and
            written to file within the browser, without needing to send the
            image files to a server. As DICOM images often contain sensitive
            information about patients, sending these files to a server over the
            internet introduces security concerns. Usually this sensitive
            information is removed from the files prior to sending them over the
            internet, but the anonymization process traditionally requires
            specialized software to be installed and configured on premises.
          </p>
          <p class="my-5">
            This web application allows removal of sensitive information both
            from DICOM headers and redaction of sensitive information from image
            pixel data. All the processing occurs on the user's (your) computer
            without sending data to a remote server, and no specialized software
            needs to be installed.
          </p>
          <p class="my-5">
            Please report any issues here:
            <a href="https://pacsbin.com/feedback"
              >https://pacsbin.com/feedback</a
            >
          </p>
          <p class="text-sm font-light my-5">&copy; Jason Hostetter 2021</p>
        </div>
      </div>
    </div>
  </div>
  <StatusDock></StatusDock>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from "vue";
import loadImages from "./loadImages";
import CaseEditor from "./case-editor/CaseEditor.vue";
import { logMemorySnapshot } from "./monitorMemory";
import CaseDef from "./types/CaseDef";
import defaultScript from "./anonymize/scripts/header-script.default";
import anonymizeInstance from "./anonymizeInstance";
import type Series from "./types/Series";
import type Instance from "./types/Instance";
import { addLog, updateStatus, progressBar } from "./logger";
import aTick from "./aTick";
import QuarantinedSeriesList from "./QuarantinedSeriesList.vue";
import ImportLocal from "./import/localfile/ImportLocal.vue";
import DicomWeb from "./import/dicomweb/DicomWeb.vue";
import WorkflowStepper from "./WorkflowStepper.vue";
import StatusDock from "./StatusDock.vue";
import ValuesHistogram from "./ValuesHistogram.vue";
import selectFrames from "./case-editor/selectFrames";
import AppLogo from "./AppLogo.vue";
import PacsbinUpload from "./export/PacsbinUpload.vue";
import ZipDownload from "./export/ZipDownload.vue";
import streamCompressInstance from "./export/stream-compress-instance";
import * as p10Staging from "./opfs/p10-staging";
import * as instanceSource from "./cornerstone/instance-source";
import { caseRedactionSummary, useRedactionVersion } from "./pixel-redaction";
import UserSession from "./UserSession.vue";
import ThemeToggle from "./ThemeToggle.vue";
import { initOPFSSession, cleanupOPFSSession } from "./opfs/opfs-store";
import {
  cornerstone,
  setCornerstoneCacheLimit,
} from "./cornerstone/cornerstone-setup.js";
import {
  getUserSession,
  isLoggedIn,
  isLoggedOut,
  userSession,
  hasRoomInAccount,
  maxStudies,
  studyCount,
} from "./user-session";
import getRemoteUrl from "./get-remote-url";
import { Icon } from "@iconify/vue";
import getConfig from "./pacsbin-v2/getOrgConfig";
import { OrgConfig } from "./import/dicomweb/types";
import getUploadCreds from "./export/pacsbin-v2/get-upload-creds";

/**
 * Constants
 */
const buildMode = import.meta.env.MODE;
const packageVersion = __APP_VERSION__;

/**
 * Organization
 */
const orgId = new URLSearchParams(window.location.search).get("org");
const orgConfig = ref<OrgConfig | null>();

/**
 * Storage provider
 */
const storageProvider = ref<string | null>(null);

// Register cleanup handlers at top-level so they survive component unmount
window.addEventListener(
  "pagehide",
  () => {
    void cleanupOPFSSession();
  },
  { capture: true }
);
window.addEventListener("beforeunload", () => {
  void cleanupOPFSSession();
});

/**
 * Check for user session
 */
onBeforeMount(async () => {
  // OPFS is used only to spool the compressed anonymized output between the write
  // and upload steps. If it's unavailable those blobs just stay in memory; the
  // image cache limit is unconditional either way, since every decoded frame can
  // be reproduced from its source file.
  try {
    await initOPFSSession();
  } catch {
    // non-fatal — staging falls back to in-memory blobs
  }
  setCornerstoneCacheLimit();

  try {
    await getUserSession();
    if (isLoggedIn.value) {
      try {
        const uploadCreds = await getUploadCreds();
        storageProvider.value = uploadCreds.storageProvider;
      } catch (err) {
        console.warn("Failed to fetch upload credentials:", err);
      }
    }
    if (orgId) {
      orgConfig.value = await getConfig();
      caseDef.value.orgId = orgConfig.value?.orgId;
    }
  } catch (err) {
    console.warn(err);
  }
});

/**
 * Element refs
 */
const fileUrlInput = ref<HTMLInputElement | null>(null);
const caseEditor = ref<InstanceType<typeof CaseEditor> | null>(null);

/**
 * Layout
 */

const tabs = [
  { key: "process-files", label: "Process" },
  { key: "anonymization-script", label: "Anonymization Script" },
  { key: "values-histogram", label: "Header Summary" },
  { key: "about", label: "About" },
];
const tab = ref("process-files");
const step = ref("load");

const exportDestination = ref<"pacsbin" | "zip">("pacsbin");

const scriptCopied = ref(false);
async function copyScriptToClipboard() {
  await navigator.clipboard.writeText(JSON.stringify(defaultScript, null, 4));
  scriptCopied.value = true;
  setTimeout(() => {
    scriptCopied.value = false;
  }, 1500);
}

function reloadPage() {
  window.location.reload();
}

onMounted(() => {
  // Navigation guard
  window.onbeforeunload = function () {
    return true;
  };
});

function nextStep() {
  if (step.value === "load") {
    step.value = "redact";
  } else if (step.value === "redact") {
    step.value = "review";
  } else if (step.value === "review") {
    step.value = "export";
    // Default to the ZIP download tab when the user isn't logged in,
    // since the Pacsbin upload destination requires an account.
    exportDestination.value = isLoggedIn.value ? "pacsbin" : "zip";
  }
}

/**
 * Images
 */
const caseDef = ref<CaseDef>({
  uid: crypto.randomUUID(),
  studies: [],
  markedForRemoval: {
    studyUIDs: [],
    seriesUIDs: [],
    instanceUIDs: [],
  },
});
const selectedSeries = ref<Series | null>(null);
const isLoadingFiles = ref(false);

const allInstances = computed(() => {
  let instances: Instance[] = [];
  for (const study of caseDef.value.studies) {
    for (const series of study.series) {
      instances = instances.concat(series.instances);
    }
  }
  return instances;
});
const anonymizedInstances = computed(() => {
  return allInstances.value.filter((i) => i.instanceData.anonymizedDicomData);
});

// Read from the redaction store — the same map the writers consult — so the review
// step reports what was actually applied to the exported pixels rather than what
// the editor's UI state suggests. `redactionVersion` makes this re-evaluate when
// rectangles are committed or cleared.
const redactionVersion = useRedactionVersion();
const redactionSummary = computed(() => {
  redactionVersion.value;
  return caseRedactionSummary(caseDef.value);
});

const instancesToProcess = computed(() => {
  let count = 0;
  for (const study of caseDef.value.studies) {
    if (caseDef.value.markedForRemoval?.studyUIDs?.includes(study.uid)) {
      continue;
    }

    for (const series of study.series) {
      if (caseDef.value.markedForRemoval?.seriesUIDs?.includes(series.uid)) {
        continue;
      }

      for (const instance of series.instances) {
        if (
          caseDef.value.markedForRemoval?.instanceUIDs?.includes(instance.uid)
        ) {
          continue;
        }

        count++;
      }
    }
  }
  return count;
});

async function loadFiles(fileList: File[], silent: boolean = false) {
  const fileUrl = fileUrlInput.value?.value;
  const urls = fileUrl ? [fileUrl] : undefined;
  await loadImages(
    { urls, files: fileList },
    caseDef,
    silent,
    storageProvider.value || undefined
  );
}

function getTotalFrames(): number {
  return allInstances.value.reduce(
    (sum, i) => sum + (i.frames?.length ?? 0),
    0
  );
}

async function loadFilesAndAdvance(fileList: File[]) {
  isLoadingFiles.value = true;
  try {
    await loadFiles(fileList);
    advanceAfterLoad();
  } finally {
    isLoadingFiles.value = false;
  }
}

/**
 * One batch of DICOMweb-retrieved instances. Called repeatedly by the importer
 * while retrieval continues, and awaited by it — the counts come back so it can
 * report a single total at the end instead of one message per batch.
 *
 * Silent: the importer owns the progress bar and status line for this flow.
 */
function loadRetrievedSources(
  sources: { bytes: ArrayBuffer; name: string }[]
) {
  return loadImages(
    { buffers: sources },
    caseDef,
    true,
    storageProvider.value || undefined
  );
}

/**
 * Move to the next step once everything is in the case. For DICOMweb this is the
 * importer's `complete`, which now fires only after the last retrieved instance
 * has actually been processed — previously it raced the in-flight loads, so the
 * snapshot below undercounted and series kept growing after the user moved on.
 */
function advanceAfterLoad() {
  logMemorySnapshot("post-load", getTotalFrames());
  if (caseDef.value.studies.length === 0) {
    return;
  }
  setSelectedSeries(caseDef.value.studies[0].series[0]);
  nextStep();
}

function setSelectedSeries(series: Series) {
  selectedSeries.value = series;
  selectedSeries.value.hasBeenSelected = true;
}

/**
 * Apply edits made to series (trim, interval), as well as instance, series or study removal. Do
 * this in the top level app rather than the Edit component to make the control flow consistent,
 * i.e. going to the next step performs the destructive action, whereas all the actions in the Edit
 * component are reversible.
 */
async function applyEdits() {
  // Apply `trim` (and, when enabled, `interval`) operations to series. These
  // operate at frame granularity: a multiframe instance can be trimmed to a
  // subset of its frames, and instances left with no frames are dropped.
  for (let study of caseDef.value.studies) {
    for (let series of study.series) {
      const selected = selectFrames(series.instances, {
        trimStart: series.trimStart,
        trimEnd: series.trimEnd,
        includeInterval: series.includeInterval,
      });
      series.instances = selected.map(({ instance, frames }) => {
        instance.frames = frames;
        instance.numberOfFrames = frames.length;
        return instance;
      });
      // The trim is now baked into instance.frames; clear the params so a
      // later (read-only) viewer mount doesn't apply the same trim a second
      // time to the already-trimmed frames.
      series.trimStart = 0;
      series.trimEnd = 0;
      series.includeInterval = undefined;
    }
  }

  // Remove studies, series, and instances marked for removal by user
  const { markedForRemoval } = caseDef.value;
  if (markedForRemoval?.studyUIDs) {
    for (let studyUID of markedForRemoval.studyUIDs) {
      const studyIndex = caseDef.value.studies.findIndex(
        (study) => study.uid === studyUID
      );
      caseDef.value.studies.splice(studyIndex, 1);
    }
  }

  if (markedForRemoval?.seriesUIDs && markedForRemoval.seriesUIDs.length > 0) {
    for (let study of caseDef.value.studies) {
      study.series = study.series.filter(
        (ser) => !markedForRemoval.seriesUIDs.includes(ser.uid)
      );
    }
  }

  if (
    markedForRemoval?.instanceUIDs &&
    markedForRemoval.instanceUIDs.length > 0
  ) {
    for (let study of caseDef.value.studies) {
      for (let series of study.series) {
        series.instances = series.instances.filter(
          (instance) => !markedForRemoval.instanceUIDs.includes(instance.uid)
        );
      }
    }
  }

  // Remove any studies without series or series without instances
  caseDef.value.studies = caseDef.value.studies.filter(
    (study) => study.series.length > 0
  );
  caseDef.value.studies.forEach((study) => {
    study.series = study.series.filter((ser) => ser.instances.length > 0);
  });

  // Nothing to clean up here any more: redaction is a set of rectangles, and undo
  // is deleting them, so there are no pre-redaction pixel backups to drop. The
  // rectangles themselves must survive — they're what the export applies.
}

// const scriptRef = ref(defaultScript);
async function anonymizeInstances() {
  // Use for...of instead of Promise.all to not attempt to anonymize serially instead of in parallel
  progressBar.value.start(allInstances.value.length);
  for (const instance of allInstances.value) {
    await aTick();
    try {
      anonymizeInstance(instance);
      addLog(
        "info",
        `${instance.imageId}: Anonymized headers`,
        instance.anonymizationLogs
      );
    } catch (err: any) {
      const errMessage =
        err.message ||
        err.error?.message ||
        JSON.stringify(err, Object.getOwnPropertyNames(err));
      if (errMessage && typeof errMessage === "string") {
        addLog(
          "error",
          `Error anonymizing image ${instance.imageId}: ${errMessage}`
        );
      } else {
        addLog(
          "error",
          `Error anonymizing image ${instance.imageId}: Unknown error`
        );
      }
      instance.anonymizationError = true;
      delete instance.instanceData.anonymizedDicomData;
      console.error(err);
    } finally {
      progressBar.value.numDone += 1;
    }
  }

  // Update metadata
  caseDef.value.studies.forEach((study) => {
    const firstStudyInstance =
      study.series[0].instances[0].instanceData.anonymizedDicomData;
    study.studyDate = firstStudyInstance?.dict["00080020"]?.Value[0];
    study.studyInstanceUID = firstStudyInstance?.dict["0020000D"]?.Value[0];
    study.series.forEach((series) => {
      series.seriesInstanceUID =
        series.instances[0].instanceData.anonymizedDicomData?.dict[
          "0020000E"
        ]?.Value[0];
      series.instances.forEach((instance) => {
        instance.sopInstanceUID =
          instance.instanceData.anonymizedDicomData?.dict["00080018"]?.Value[0];
      });
    });
  });

  progressBar.value.reset();
  updateStatus("Anonymization complete");
}

/**
 * Write modified pixel data and anonymized dataset to dicomP10 buffer
 */
async function writeAnonymizedInstancesToBuffer() {
  // Start from an empty image cache so every frame written to the output is
  // decoded fresh through the current redaction rectangles. Cheap now that frames
  // are always reproducible from their source files.
  try {
    cornerstone.imageCache.purgeCache();
  } catch {
    // non-fatal
  }

  progressBar.value.start(anonymizedInstances.value.length);
  for (const [i, instance] of anonymizedInstances.value.entries()) {
    await aTick();
    const n = anonymizedInstances.value.length;
    // Each instance is one progress unit (base = i). Frame streaming +
    // compression happen together in streamCompressInstance and fill [i, i+0.9);
    // staging fills the rest. (Compression and staging have no internal progress
    // events, so we just step the bar and update the status.)
    updateStatus(`Writing instance ${i + 1} of ${n} to file.`);
    // Streams pixel frames straight through gzip — the full uncompressed P10 is
    // never materialized (avoids dcmjs .write()'s multi-copy spike). Sets
    // dicomP10Compressed directly; falls back to monolithic write+gzip when the
    // browser lacks CompressionStream.
    //
    // Each frame is checked against its committed redaction rectangles on the way
    // out (see export/verify-redaction.ts). A failure there means we were about to
    // write pixels the user believes are redacted, so it aborts the whole export
    // rather than skipping the instance — a partial study that silently omits the
    // one file that failed verification is a worse outcome than stopping.
    await streamCompressInstance(instance, async (done, total) => {
      progressBar.value.numDone = i + 0.9 * (done / total);
      // Yield a macrotask so the browser repaints the bar between frames.
      await aTick();
    });

    // Stage the compressed bytes to OPFS and drop the in-memory Blob.
    // If OPFS is unavailable or full, keep the Blob in memory (fallback).
    const blob = instance.instanceData.dicomP10Compressed;
    if (blob) {
      updateStatus(`Saving instance ${i + 1} of ${n}.`);
      progressBar.value.numDone = i + 0.95;
      await aTick();
      const staged = await p10Staging.stage(instance.uid, blob);
      if (staged) {
        instance.instanceData.p10Staged = true;
        instance.instanceData.dicomP10Compressed = undefined;
      }
    }
    progressBar.value.numDone = i + 1;
  }
  // Drop the last parsed source file the writer left hot.
  instanceSource.releaseAll();
  progressBar.value.reset();
  updateStatus("Files ready for export");
}

/**
 * Throw away every P10 produced by a write pass — in memory and in OPFS staging.
 * Used when a pass aborts partway, so the export paths (which happily read either
 * one) can't pick up files from a run that didn't finish.
 */
async function discardWrittenFiles() {
  for (const instance of allInstances.value) {
    instance.instanceData.dicomP10Compressed = undefined;
    instance.instanceData.dicomP10AnonymizedArrayBuffer = null;
    if (instance.instanceData.p10Staged) {
      instance.instanceData.p10Staged = false;
      try {
        await p10Staging.evict(instance.uid);
      } catch {
        // best effort — the flag above is what the export paths read
      }
    }
  }
}

/**
 * After edits are done and PHI attestation made, process all files including header anonymization
 * and writing finalized data to P10 buffer.
 */
async function finalizeAndPrepareForExport() {
  if (instancesToProcess.value === 0) return;
  // The user may still have the redaction tool open with boxes drawn but not
  // applied. Those boxes are visible on screen, so committing them is what they
  // plainly intend; dropping them (the old behavior) exports the pixels underneath.
  caseEditor.value?.commitPendingRedactions();
  await applyEdits();
  logMemorySnapshot("post-edit", getTotalFrames());

  // If selected series was removed, select a new series
  const seriesUids = caseDef.value.studies.reduce((uids, study) => {
    return uids.concat(study.series.map((s) => s.uid));
  }, [] as string[]);
  if (!seriesUids.includes(selectedSeries.value?.uid || "")) {
    setSelectedSeries(caseDef.value.studies[0]?.series[0]);
  }

  await anonymizeInstances();
  logMemorySnapshot("post-anonymize", getTotalFrames());
  try {
    await writeAnonymizedInstancesToBuffer();
  } catch (err: any) {
    // Fail closed: stay on this step so nothing can be exported, and say why.
    // Redaction verification failures land here (see writeAnonymizedInstancesToBuffer).
    // Discard whatever the aborted pass already wrote, so no half-finished set of
    // files can be picked up by a later export path.
    await discardWrittenFiles();
    progressBar.value.reset();
    const message = err?.message || String(err);
    addLog("error", `Export halted before writing files: ${message}`);
    updateStatus("Export halted — see errors below");
    console.error(err);
    return;
  }
  logMemorySnapshot("post-write", getTotalFrames());
  nextStep();
}
</script>

<style scoped>
section {
  position: relative;
  padding: 0.5rem;
}

.section-head {
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 700;
  color: var(--color-base-content);
  opacity: 0.7;
  margin: 0.25rem 0 1.25rem 0;
}

.section-content {
  /* padding-left: 0.5rem; */
  padding-top: 1rem;
  color: var(--color-base-content);
}

#dropzone.dragover {
  background-color: color-mix(in oklab, var(--color-success) 10%, transparent);
}

.option-checkbox {
  margin: 0.5rem;
  padding-left: 20px;
  text-indent: -20px;
}
</style>
